import { App } from "../../../models";
import { getSubscription } from "../../../utils";
import { AppUser, Product } from "../../api";
import {
  CostProduct,
  CostStatus,
  DEFAULT_MAX_FOOD_COST,
  DEFAULT_TARGET_FOOD_COST,
} from "../../../utils/recipeCost";

/** Businesses whose recipes are food: costing shows food cost wording. */
export const FOOD_APPS: Array<string> = [
  App.RESTAURANT,
  App.RESTOBAR,
  App.BAR,
  App.BAKERY,
  App.ICECREAM,
  App.SNACKSHOP,
];

/**
 * The location's row of a product: the branch's when a branch reads it, the
 * store's when a store does; none at the top level.
 */
const locationRow = (p: any) =>
  (p?.branchProducts?.length > 0 && p.branchProducts[0]) ||
  (p?.storeProducts?.length > 0 && p.storeProducts[0]) ||
  null;

/**
 * A product's cost and price for costing: at a branch, the branch's when it
 * has the product, else the product's own (as sales and purchases do). At the
 * top level, the product's own.
 */
export const locationCostOf = (p: any, isBranch: boolean) => {
  const row = isBranch ? locationRow(p) : null;
  return {
    unitCost: row ? row.unitCost : p?.unitCost,
    unitPrice: row ? row.unitPrice : p?.unitPrice,
  };
};

export const toCostProduct = (
  p: Product | any,
  isBranch: boolean,
): CostProduct => ({
  id: p?.id,
  name: p?.name,
  unit: p?.unit,
  isService: !!p?.isService,
  units: p?.units || [],
  pUnits: p?.pUnits || [],
  unitCost: locationCostOf(p, isBranch).unitCost,
});

export interface CostingSettings {
  target: string;
  max: string;
  isTaxIncludedInPrice: boolean;
  isFood: boolean;
  isBranch: boolean;
}

/**
 * Whether the business's recipes are food: the app it runs, or for the
 * general app, any food app it subscribes to.
 */
export const isFoodBusiness = (app: string, subscribedApps: Array<string>) =>
  FOOD_APPS.includes(app) ||
  ((app === App.APP || app === App.MYAPP || !app) &&
    subscribedApps.some((a) => FOOD_APPS.includes(a)));

/**
 * Targets and the tax setting are the business's: the top level sets them for
 * every branch.
 */
export const getCostingSettings = (appUser: AppUser): CostingSettings => {
  const own: any = appUser?.user?.client || {};
  const client: any = own.isBranch && own.parent ? own.parent : own;
  const subscription = getSubscription(appUser);
  return {
    target: client.targetFoodCost ?? DEFAULT_TARGET_FOOD_COST,
    max: client.maxFoodCost ?? DEFAULT_MAX_FOOD_COST,
    isTaxIncludedInPrice: !!client.isTaxIncludedInPrice,
    isFood: isFoodBusiness(
      process.env.REACT_APP_APP || App.APP,
      subscription ? Object.keys(subscription) : [],
    ),
    isBranch: !!appUser?.user?.currentClient?.isBranch,
  };
};

/** Translation key of a label, in food or production wording. */
export const costKey = (key: string, isFood: boolean) =>
  `Costing.${isFood ? "Food" : "Production"}.${key}`;

export const statusColor = (status: CostStatus) =>
  status === "good"
    ? "success"
    : status === "warning"
      ? "warning"
      : status === "danger"
        ? "error"
        : "default";

export const statusKey = (status: CostStatus) =>
  status === "good"
    ? "Costing.Status.Good"
    : status === "warning"
      ? "Costing.Status.Warning"
      : status === "danger"
        ? "Costing.Status.Danger"
        : "Costing.Status.NoPrice";
